import React, { useState, useContext, useEffect, useCallback } from 'react';
import yaml from 'js-yaml';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';
import {
  Box, Select, Spinner, Text, VStack, Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon, Flex, Badge, IconButton, Grid
} from "@chakra-ui/react";
import { EditIcon, ViewIcon, Search2Icon } from '@chakra-ui/icons';

function ClientConfigs({ showInactive, onEdit, onCompare, refreshKey }) {
  const [orgs, setOrgs] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientConfigs, setClientConfigs] = useState([]);

  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingClientConfigs, setIsLoadingClientConfigs] = useState(false);

  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');

  const { accessToken, toast } = useContext(AppContext);

  const loadOrgs = useCallback(async () => {
    setIsLoadingOrgs(true);
    try {
      const response = await callApi('/admin/config/orgs-with-client-config-counts', accessToken);
      const data = await response.json();
      if (response.ok) {
        setOrgs(data);
      } else {
        throw new Error(data.error || 'Failed to fetch organizations');
      }
    } catch (err) {
      toast({ title: 'Error loading organizations', description: err.message, status: 'error' });
    } finally {
      setIsLoadingOrgs(false);
    }
  }, [accessToken, toast]);

  const loadClientsForOrg = useCallback(async (orgId) => {
    if (!orgId) return;
    setIsLoadingClients(true);
    setClients([]);
    try {
      const response = await callApi(`/admin/config/clients/${orgId}`, accessToken);
      const data = await response.json();
      if (response.ok) {
        setClients(data);
      } else {
        throw new Error(data.error || 'Failed to fetch clients');
      }
    } catch (err) {
      toast({ title: 'Error loading clients', description: err.message, status: 'error' });
    } finally {
      setIsLoadingClients(false);
    }
  }, [accessToken, toast]);

  const loadConfigsForClient = useCallback(async (clientId) => {
    if (!clientId) return;
    setIsLoadingClientConfigs(true);
    setClientConfigs([]);
    try {
      const response = await callApi(`/admin/config/client-configurations/client/${clientId}`, accessToken);
      const data = await response.json();
      if (response.ok) {
        setClientConfigs(data);
      } else {
        throw new Error(data.error || 'Failed to fetch client configurations');
      }
    } catch (err) {
      toast({ title: 'Error loading configurations', description: err.message, status: 'error' });
    } finally {
      setIsLoadingClientConfigs(false);
    }
  }, [accessToken, toast]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs, refreshKey]);

  const handleOrgChange = (e) => {
    const orgId = e.target.value;
    setSelectedOrgId(orgId);
    setSelectedClientId('');
    setClientConfigs([]);
    setClients([]);
    if (orgId) {
      loadClientsForOrg(orgId);
    }
  };

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    setSelectedClientId(clientId);
    if (clientId) {
      loadConfigsForClient(clientId);
    } else {
      setClientConfigs([]);
    }
  };

  const diffViewerStyles = { content: { textAlign: 'left' } };
  const yamlDumpOptions = { lineWidth: -1, noRefs: true, skipInvalid: true };

  const renderClientConfigsTimeline = () => {
    if (isLoadingClientConfigs) {
      return <Flex justify="center" p={10}><Spinner /></Flex>;
    }
    if (!selectedClientId) {
      return <Text>Please select a client to view its configuration history.</Text>;
    }
    if (clientConfigs.length === 0) {
      return <Text>No configurations found for this client.</Text>;
    }

    const clientData = {
      client_name: clientConfigs.length > 0 ? clientConfigs[0].client_name : '',
      versions: clientConfigs,
    };

    return (
      <VStack spacing={6} align="stretch" mt={4}>
        {clientData.versions.map((config, i) => {
          const isMostRecent = i === 0;
          const previousConfig = clientData.versions[i + 1];
          const currentYaml = yaml.dump(config.configuration || {}, yamlDumpOptions);
          const previousYaml = previousConfig ? yaml.dump(previousConfig.configuration || {}, yamlDumpOptions) : "";

          return (
            <Box key={config.id} borderWidth="1px" borderRadius="md" p={4} boxShadow="sm">
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Text fontWeight="bold" fontSize="md">
                    {clientData.client_name} - {new Date(config.created_at).toLocaleString()}
                  </Text>
                  <Badge colorScheme={config.active ? 'green' : 'gray'}>
                    {config.active ? 'Active' : 'Inactive'}
                  </Badge>
                </Box>
                {isMostRecent ? (
                  <IconButton
                    icon={<EditIcon />} size="sm" onClick={() => onEdit(config, 'client', false)}
                    aria-label="Edit client config" title="Edit configuration (creates new version)"
                  />
                ) : (
                  <Flex>
                    <IconButton
                      icon={<ViewIcon />} size="sm" onClick={() => onEdit(config, 'client', true)}
                      aria-label="View full configuration" title="View full configuration"
                    />
                    <IconButton
                      icon={<Search2Icon />} size="sm" onClick={() => onCompare(clientData.versions[0], config)}
                      aria-label="Compare to active version" title="Compare to active version" ml={2}
                    />
                  </Flex>
                )}
              </Flex>
              
              {showInactive && previousConfig && (
                <Box mt={4} borderWidth="1px" borderRadius="md" overflow="hidden">
                  <ReactDiffViewer
                    oldValue={previousYaml} newValue={currentYaml} splitView={false}
                    hideLineNumbers={true} showDiffOnly={true} styles={diffViewerStyles}
                  />
                </Box>
              )}
              {!previousConfig && !isMostRecent && showInactive && (
                <Box mt={4} borderWidth="1px" borderRadius="md" overflow="hidden">
                  <Text p={2} fontSize="sm">This is the first version.</Text>
                </Box>
              )}
            </Box>
          );
        })}
      </VStack>
    );
  };

  return (
    <VStack spacing={4} align="stretch">
      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <Select
          placeholder="Select an Organization..."
          value={selectedOrgId}
          onChange={handleOrgChange}
          isDisabled={isLoadingOrgs}
        >
          {orgs.map(org => (
            <option key={org.id} value={org.id}>
              {org.name} ({org.client_count} {org.client_count === 1 ? 'client' : 'clients'})
            </option>
          ))}
        </Select>
        <Select
          placeholder={isLoadingClients ? "Loading clients..." : "Select a Client..."}
          value={selectedClientId}
          onChange={handleClientChange}
          isDisabled={!selectedOrgId || isLoadingClients || clients.length === 0}
        >
          {clients.map(client => (
            <option key={client.id} value={client.id}>{client.name}</option>
          ))}
        </Select>
      </Grid>
      <Box>
        {renderClientConfigsTimeline()}
      </Box>
    </VStack>
  );
}

export default ClientConfigs;